import { useState } from "react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { CURRENT_TOS_VERSION } from "../lib/tos";
import Button from "./ui/Button";

/**
 * Blocking Terms of Service clickwrap. Rendered as a full-screen modal for
 * logged-in users who have not accepted the current ToS version (first login,
 * or after a ToS version bump). The user cannot proceed into the app without
 * checking the box and agreeing; declining logs them out.
 */
const TosClickwrapModal: React.FC = () => {
  const { acceptTos, logout } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    const ok = await acceptTos(CURRENT_TOS_VERSION);
    // On success the user is refreshed and this modal unmounts. On failure the
    // AuthContext surfaces a toast; re-enable so they can retry.
    if (!ok) setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tos-title"
      aria-describedby="tos-desc"
    >
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-8 flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
            <DocumentTextIcon className="w-6 h-6 text-vein-red-500" />
          </div>
          <h2 id="tos-title" className="text-xl font-bold text-gray-900">
            Review our Terms
          </h2>
        </div>

        <p id="tos-desc" className="text-sm text-gray-600 leading-relaxed">
          Before you continue, please review and accept our Terms of Service and
          Privacy Policy. You need to agree to keep using Vein.
        </p>

        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={submitting}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-vein-red-500 focus:ring-vein-red-500"
          />
          <span className="text-sm text-gray-700 leading-relaxed">
            I agree to the{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-vein-red-600 font-medium underline hover:text-vein-red-700"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-vein-red-600 font-medium underline hover:text-vein-red-700"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>

        <div className="flex flex-col gap-2">
          <Button
            size="lg"
            fullWidth
            type="button"
            onClick={handleAccept}
            disabled={!agreed || submitting}
            aria-busy={submitting}
          >
            {submitting ? "Saving…" : "Agree & Continue"}
          </Button>
          <Button
            variant="ghost"
            fullWidth
            type="button"
            onClick={() => logout()}
            disabled={submitting}
          >
            Decline and log out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TosClickwrapModal;
